"use client";

import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useState,
} from "react";
import { useRouter } from "next/navigation";
import { createLogAdapter } from "@/lib/logger/console-adapter";

export type ViewMode = "standard" | "pro" | "focus";

interface ViewContextType {
	viewMode: ViewMode;
	focusTokenId: string;
	setViewMode: (mode: ViewMode) => void;
	setFocusTokenId: (tokenId: string) => void;
}

const ViewContext = createContext<ViewContextType | undefined>(undefined);
const uiLog = createLogAdapter("contexts/view-context.tsx");

export function ViewProvider({ children }: { children: ReactNode }) {
	const [viewMode, setViewModeState] = useState<ViewMode>("standard");
	const [focusTokenId, setFocusTokenIdState] = useState<string>("2rfi");
	const router = useRouter();

	const syncViewCookies = (mode: ViewMode, tokenId: string) => {
		const maxAge = 60 * 60 * 24 * 365;
		document.cookie = `odin_view_mode=${mode}; Path=/; Max-Age=${maxAge}; SameSite=Lax`;
		document.cookie = `odin_focus_token_id=${tokenId}; Path=/; Max-Age=${maxAge}; SameSite=Lax`;
	};

	// Load settings from local storage
	useEffect(() => {
		try {
			const savedMode = localStorage.getItem("odin_view_mode") as ViewMode;
			const savedTokenId = localStorage.getItem("odin_focus_token_id");
			const effectiveMode =
				savedMode && ["standard", "pro", "focus"].includes(savedMode)
					? savedMode
					: "standard";
			const effectiveTokenId = savedTokenId || "2rfi";

			setViewModeState(effectiveMode);
			setFocusTokenIdState(effectiveTokenId);
			syncViewCookies(effectiveMode, effectiveTokenId);
		} catch (error) {
			uiLog.error("Error loading view settings from localStorage:", error);
		}
	}, []);

	const setViewMode = (mode: ViewMode) => {
		try {
			setViewModeState(mode);
			localStorage.setItem("odin_view_mode", mode);
			syncViewCookies(mode, focusTokenId);
			
			if (mode === "standard") router.push("/plaza");
			else if (mode === "pro") router.push("/data");
			else if (mode === "focus") router.push(`/plaza/token/${focusTokenId}`);
			
		} catch (error) {
			uiLog.error("Error saving view mode:", error);
		}
	};

	const setFocusTokenId = (tokenId: string) => {
		try {
			setFocusTokenIdState(tokenId);
			localStorage.setItem("odin_focus_token_id", tokenId);
			syncViewCookies(viewMode, tokenId);
			if (viewMode === "focus") {
				router.push(`/plaza/token/${tokenId}`);
			}
		} catch (error) {
			uiLog.error("Error saving focus token id:", error);
		}
	};

	return (
		<ViewContext.Provider
			value={{
				viewMode,
				focusTokenId,
				setViewMode,
				setFocusTokenId,
			}}
		>
			{children}
		</ViewContext.Provider>
	);
}

export function useView() {
	const context = useContext(ViewContext);
	if (context === undefined) {
		throw new Error("useView must be used within a ViewProvider");
	}
	return context;
}
